import { useTokenizePaymentMethodForm } from "@/store/profile/forms/useTokenizePaymentMethodForm";
import { getErrorMessage } from "@/lib/helpers/string";
import { showToast } from "@/components/ui/toast/show-toast";
import { useAuthState } from "@/store/auth";
import { useClientStore } from "@/store/auth/client";
import { useDeletePaymentMethodStore } from "@/store/profile/deletePaymentMethod";
import { useUpdateClientPreferencesStore } from "@/store/profile/updateClientPreferences";
import { useEffect, useState } from "react";
import type { UserPaymentMethod } from "@/types/users";
import { PaymentScreen } from "./payment-screen";
import { usePayInvoiceStore } from "@/store/invoices/payInvoice";
import { useInvoiceState } from "@/store/invoices";
import { useRoute } from "@/components/shared/router";
import { useClientApplyCreditStore } from "@/store/profile/clientApplyCredit";
import { useInvoiceDetailStore } from "@/store/invoices/invoiceDetail";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { useClientPaymentMethodStore } from "@/store/auth/clientPaymentMethod";
import { CreditTypeEnum, StripeCardBrandsEnum } from "@/__generated__/graphql";

export type PaymentScreeProps = {
  handleRemoveConfirmation: () => void;
  loading: boolean;
  onDeleteCard: () => Promise<void>;
  paymentMethod: UserPaymentMethod | null;
  infoCardDesc: string;
  handleCloseRemoveConfirmation: () => void;
  openRemoveConfirmation: boolean;
  setOpenRemoveConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  onPay: () => Promise<void>;
  creditCardPayment: boolean;
};

export const Payment = () => {
  const { push, pull } = useRoute();
  const { isMobile } = useWindowDimensions();
  const {
    data: { userInfo },
  } = useAuthState();
  const {
    data: {
      invoiceId,
      creditCardScreenModal,
      applyMainCredit,
      applyMassageCredit,
      appliedMainCreditAmount,
      appliedMassageCreditAmount,
    },
    showPaymentMethodModal,
  } = useInvoiceState();

  const [openRemoveConfirmation, setOpenRemoveConfirmation] =
    useState<boolean>(false);

  const { fetch: client } = useClientStore();
  const { fetch: deletePaymentMethod, loading: deletePaymentMethodLoading } =
    useDeletePaymentMethodStore();
  const {
    fetch: updateClientPreferences,
    loading: updateClientPreferencesLoading,
  } = useUpdateClientPreferencesStore();
  const { fetch: payInvoice, loading: payInvoiceLoading } =
    usePayInvoiceStore();

  const { fetch: clientApplyCredit, loading: clientApplyLoading } =
    useClientApplyCreditStore();

  const { refetch } = useInvoiceDetailStore();

  const { reset } = useTokenizePaymentMethodForm();

  const {
    fetch: fetchClientPaymentMethod,
    loading: fetchClientPaymentMethodLoading,
  } = useClientPaymentMethodStore();

  useEffect(() => {
    if (userInfo.id) {
      fetchClientPaymentMethod({ requestPayload: { id: userInfo.id } });
    }
  }, [userInfo.id]);

  const paymentMethod = userInfo.paymentMethod;

  const onDeleteCard = async () => {
    if (paymentMethod) {
      try {
        const deleteRes = await deletePaymentMethod(
          {
            requestPayload: {
              input: {
                clientId: userInfo.id,
                paymentMethodId: paymentMethod.id,
              },
            },
          },
          { selfHandleError: true },
        );

        if (deleteRes.error) {
          throw new Error(deleteRes.error);
        }

        const updatePreferences = await updateClientPreferences(
          {
            requestPayload: {
              input: {
                clientId: userInfo.id,
                preferences: {
                  autoPay: false,
                },
              },
            },
          },
          { selfHandleError: true },
        );

        if (updatePreferences.error) {
          throw new Error(updatePreferences.error);
        }

        setOpenRemoveConfirmation(false);

        fetchClientPaymentMethod({
          requestPayload: {
            id: userInfo.id,
          },
        });

        if (isMobile) {
          pull();
        } else {
          push({ pageKey: "AddCreditCard" });
        }
        reset();
      } catch (error) {
        const errMsg = getErrorMessage(error, "Error deleting payment method");
        showToast({
          title: errMsg,
          type: "error",
        });
      }
    }
  };

  const handleRemoveConfirmation = () => {
    setOpenRemoveConfirmation(true);
  };

  const handleCloseRemoveConfirmation = () => {
    setOpenRemoveConfirmation(false);
  };

  const infoCardDesc =
    "We impose a surcharge of 3.4% + $0.5 for Debit & Credit Cards only, which goes towards card processing costs and is not greater than our cost of acceptance. Unfortunately, we currently don't accept Visa, but you can proceed with other payment networks. Alternatively, you can manually pay via PayNow directly with the invoice which does not have a processing fee.";

  const handlePay = async () => {
    try {
      // Check if any credits are being applied
      const hasCreditsApplied = applyMainCredit || applyMassageCredit;

      if (hasCreditsApplied) {
        const creditAllocations = [];

        // Add main credit allocation if applied
        if (applyMainCredit && appliedMainCreditAmount > 0) {
          const mainCreditAccount = userInfo.creditAccounts.find(
            (credit) => credit.creditType === CreditTypeEnum.General,
          );

          if (mainCreditAccount) {
            creditAllocations.push({
              creditAccountId: mainCreditAccount.id,
              amount: appliedMainCreditAmount,
            });
          }
        }

        // Add massage credit allocation if applied
        if (applyMassageCredit && appliedMassageCreditAmount > 0) {
          const massageCreditAccount = userInfo.creditAccounts.find(
            (credit) => credit.creditType === CreditTypeEnum.Department,
          );

          if (massageCreditAccount) {
            creditAllocations.push({
              creditAccountId: massageCreditAccount.id,
              amount: appliedMassageCreditAmount,
            });
          }
        }

        // Apply credits if any allocations exist
        if (creditAllocations.length > 0) {
          const clientApplyCreditRes = await clientApplyCredit(
            {
              requestPayload: {
                input: {
                  invoiceId: invoiceId,
                  creditAllocations,
                },
              },
            },
            { selfHandleError: true },
          );

          if (clientApplyCreditRes.error)
            throw new Error(clientApplyCreditRes.error);

          showToast({
            title: "Payment successful using main credit",
            type: "success",
          });

          refetch();
        }
      }

      // Proceed with credit card payment
      if (!paymentMethod) {
        showToast({
          title: "Please add a credit card to pay the invoice.",
          type: "error",
        });
        return;
      }

      if (paymentMethod.brand === StripeCardBrandsEnum.Visa) {
        showToast({
          title: "Visa cards are not accepted. Please use another card.",
          type: "error",
        });
        return;
      }

      const res = await payInvoice(
        { requestPayload: { input: { invoiceId } } },
        { selfHandleError: true },
      );

      if (res.error) {
        throw new Error(res.error);
      }

      const successMsg = `Success pay Invoice, ${invoiceId}`;
      showToast({
        title: successMsg,
        type: "success",
      });

      showPaymentMethodModal();
      client({
        requestPayload: {
          id: userInfo.id,
        },
      });
      push({ pageKey: "invoices" });
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error processing payment");
      showToast({ title: errMsg, type: "error" });
    }
  };

  const loading =
    deletePaymentMethodLoading ||
    payInvoiceLoading ||
    updateClientPreferencesLoading ||
    clientApplyLoading ||
    fetchClientPaymentMethodLoading;

  return (
    <PaymentScreen
      handleRemoveConfirmation={handleRemoveConfirmation}
      loading={loading}
      onDeleteCard={onDeleteCard}
      paymentMethod={paymentMethod}
      infoCardDesc={infoCardDesc}
      handleCloseRemoveConfirmation={handleCloseRemoveConfirmation}
      openRemoveConfirmation={openRemoveConfirmation}
      setOpenRemoveConfirmation={setOpenRemoveConfirmation}
      onPay={handlePay}
      creditCardPayment={creditCardScreenModal}
    />
  );
};
